<script lang="ts" setup>
import { ref, computed, Ref, inject, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { TextFieldTypes, TextEntry, instanceOfTextEntry, ValidationTypes, TextType } from '.';
import { Option } from '@/helpers/Interfaces';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useEvents } from '@/plugins/events';
import SettingsService from '@/modules/core/common/services/SettingsService';
import AiRegexModal from './AiRegexModal.vue';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import debounce from 'lodash/debounce';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'text-blueprint',
    components: {
        ...properties,
        AiRegexModal
    }
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const level = inject<number>('level', 0);
const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new TextEntry()) as Ref<TextEntry>;
const isAiEnabled = ref(false);
const aiModalRef = ref<InstanceType<typeof AiRegexModal> | null>(null);

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

entryData.value = props.form.document.initEntry(props.blueprint, entryData.value, instanceOfTextEntry, props.index);

onBeforeMount(async () =>
{
    if (props.parent?.type === 'table')
        useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(reloadControl);
    else
        useControlReload($events, props.blueprint.name, level).eventOn(reloadControl);

    entryData.value.isHeader = props.blueprint.isHeader;
    entryData.value.isSummary = props.blueprint.isSummary;

    // Load AI enabled status
    try
    {
        isAiEnabled.value = await SettingsService.getAiEnabled();
    }
    catch (error)
    {
        isAiEnabled.value = false;
    }

    if (!props.form.designMode())
    {
        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (props.parent?.type === 'table')
        useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
    else
        useControlReload($events, props.blueprint.name, level).eventOff();

    if (!props.form.designMode())
    {
        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const reloadControl = () =>
{
    entryData.value = props.form.document.initEntry(props.blueprint, entryData.value, instanceOfTextEntry, props.index);
};

const value = computed({
    get()
    {
        return entryData.value.data === null || (readonly.value && props.blueprint.defaultValue)
            ? props.form.expressions.executeExpression(props.blueprint.defaultValue)
            : entryData.value.data;
    },
    set(value: string)
    {
        entryData.value.data = value;
        emitDynamicTrigger();
    }
});

const fieldTypeOptions: Option<TextFieldTypes>[] = [
    { value: TextFieldTypes.Text, text: '[[[Tekst]]]' },
    { value: TextFieldTypes.Multiline, text: '[[[Treść]]]' },
    { value: TextFieldTypes.Password, text: '[[[Hasło]]]' }
];

const validationOptions: Option<ValidationTypes>[] = [
    { value: ValidationTypes.None, text: '[[[Brak]]]' },
    { value: ValidationTypes.Alphabetic, text: '[[[Litery]]]' },
    { value: ValidationTypes.Numeric, text: '[[[Cyfry]]]' },
    { value: ValidationTypes.Alphanumeric, text: '[[[Litery i cyfry]]]' },
    { value: ValidationTypes.RegularExpression, text: '[[[Wyrażenie regularne]]]' }
];

const isTextField = computed(() => props.blueprint.fieldType === TextFieldTypes.Text);
const isMultiline = computed(() => props.blueprint.fieldType === TextFieldTypes.Multiline);
const isPassword = computed(() => props.blueprint.fieldType === TextFieldTypes.Password);
const isRegExpValidation = computed(() =>
{
    return isTextField.value && props.blueprint.validation === ValidationTypes.RegularExpression;
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));

const emitDynamicTrigger = debounce(() =>
{
    useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
}, 1000);

const openAiModal = () =>
{
    aiModalRef.value?.showModal();
};

const onRegexGenerated = (regex: string) =>
{
    if (props.blueprint)
    {
        // eslint-disable-next-line vue/no-mutating-props
        props.blueprint.validationRule = regex;
    }
};
</script>

<template>
    <form-component-wrapper class="text-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <ideo-input-group :prepend="blueprint.affix.prepend" :append="blueprint.affix.append">
                    <input type="text" class="form-control" v-model="value" :id="blueprint.id" :placeholder="placeholder" :readonly="readonly" v-if="isTextField">
                    <textarea class="form-control" v-model="value" :id="blueprint.id" :placeholder="placeholder" :readonly="readonly" :rows="blueprint.rows" v-if="isMultiline"></textarea>
                    <input type="password" class="form-control" v-model="value" :id="blueprint.id" :placeholder="placeholder" :readonly="readonly" v-if="isPassword">
                </ideo-input-group>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>

            <!-- AI Regex Modal -->
            <AiRegexModal
                ref="aiModalRef"
                @regex-generated="onRegexGenerated"
            />
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-choice v-model="blueprint.fieldType" :options="fieldTypeOptions" :label="$t('[[[Typ pola]]]')" />
                    <div class="form-group">
                        <ideo-form-checkbox v-model="blueprint.isEncrypted" :disabled="!blueprint.isNew">{{ $t('[[[Zaszyfrowane]]]') }}</ideo-form-checkbox>
                        <small class="text-muted">{{ $t('[[[Szyfrowanie może być ustawione tylko dla nowych pól]]]') }}</small>
                        <small class="text-muted">{{ $t('[[[Dla pola szyfrowanego nie będzie możliwe wyszukiwanie i sortowanie.]]]') }}</small>
                    </div>
                    <field-condition v-model="blueprint.defaultValue" :form="form" :label="$t('[[[Wartość domyślna]]]')" type="text" />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <field-affix v-model="blueprint.affix" />
                    <field-minmax v-model="blueprint.characters" :label="$t('[[[Ilość znaków]]]')" :naturals="true" />
                    <field-numeric v-model="blueprint.rows" :label="$t('[[[Ilość wierszy]]]')" :naturals="true" :min="2" :max="20" v-if="isMultiline" />
                    <field-select v-model="blueprint.validation" :options="validationOptions" :label="$t('[[[Walidacja]]]')" v-if="isTextField" />
                    <field-text v-model="blueprint.validationRule" :invalid-feedback="() => form.schema.errorMessage(blueprint, 'validationRule')" :label="$t('[[[Wyrażenie regularne]]]')" v-if="isRegExpValidation" />
                    <div v-if="isRegExpValidation && isAiEnabled" class="mb-3">
                        <button type="button" class="btn btn-outline-primary btn-sm" @click="openAiModal">
                            <i class="fa fa-magic me-1"></i>
                            {{ $t('[[[Generuj z AI]]]') }}
                        </button>
                    </div>
                    <ideo-form-localize v-if="isRegExpValidation" v-slot="{ locale }">
                        <field-text v-model="blueprint.validationMessage[locale]" :label="$t('[[[Komunikat walidacji]]]')" />
                    </ideo-form-localize>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" />
                    <field-error :form="form" :blueprint="blueprint" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
